import { CloudUploadOutlined, Delete, EmailOutlined, ScheduleSend, Visibility } from '@mui/icons-material';
import { Badge, Box, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { MUIDataTableOptions } from 'mui-datatables';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTimeFormat, getLocalDateTime } from 'src/common';
import { ServerSideDataTable } from 'src/common/components/dataTable/serverSide';
import { DatatableColumn, FilterDataType } from 'src/common/components/dataTable/types';
import { QueryField } from 'src/store';
import { SCHEDULED_INSTANCE_STATUS_COLORS, SCHEDULED_INSTANCE_STATUS_NAMES } from '../../constants';
import { Props } from '../../container';
import { BulkReportJob } from '../../store/types';
import { DeleteBulkReportJobModal } from './DeleteBulkReportJobModal';

export const ReportingLibraryTable = (props: Props): JSX.Element | null => {
  const [pagedRequest, setPagedRequest] = useState({
    pageNumber: 1,
    pageSize: 100,
    queryFields: [] as QueryField[],
  });

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [deletingRow, setDeletingRow] = useState<BulkReportJob | null>(null);

  const history = useHistory();

  const handlePagedRequest = useCallback((newRequest: typeof pagedRequest) => {
    setPagedRequest(newRequest);
  }, []);

  useEffect(() => {
    props.fetchBulkReportList({
      draft: false,
      scheduledOnly: false,
      page: pagedRequest.pageNumber,
      pageSize: pagedRequest.pageSize,
      queryFields: pagedRequest.queryFields,
    });
  }, [pagedRequest]);

  const handleView = (id: string) => {
    history.push(`/reporting/${id}`);
  };

  const handleDelete = (job: BulkReportJob) => {
    setDeletingRow(job);
  };

  const deleteBulkReport = (id: string) => {
    props
      .deleteBulkReport(id)
      .unwrap()
      .then(() => {
        props.fetchBulkReportList({
          draft: false,
          scheduledOnly: false,
          page: pagedRequest.pageNumber,
          pageSize: pagedRequest.pageSize,
          queryFields: pagedRequest.queryFields,
        });
      });
  };

  const pageRows = props.reporting.bulk.paginatedBulkReportResults?.results ?? [];

  const rowsSelected = useMemo(() => {
    return pageRows.reduce<number[]>((acc, row, index) => {
      if (selectedRowIds.includes(row.id)) {
        acc.push(index);
      }
      return acc;
    }, []);
  }, [pageRows, selectedRowIds]);

  const tableOptions: MUIDataTableOptions = {
    filter: true,
    search: false,
    viewColumns: true,
    download: true,
    print: false,
    selectableRows: 'multiple',
    selectableRowsHideCheckboxes: false,
    pagination: true,
    serverSide: true,
    rowsSelected,
    onRowSelectionChange: (currentRowsSelected, allRowsSelected) => {
      const selectedIdsOnPage = allRowsSelected.map(({ index }) => pageRows[index]?.id).filter(Boolean) as string[];

      setSelectedRowIds((prev) => {
        const pageIds = pageRows.map((r) => r.id);

        const remaining = prev.filter((id) => !pageIds.includes(id));

        return [...remaining, ...selectedIdsOnPage];
      });
    },
  };

  const getReportingLibraryColumns = (handleView: (id: string) => void, handleDelete: (job: BulkReportJob) => void) => {
    const columns: DatatableColumn[] = useMemo(
      () => [
        {
          name: 'name',
          label: 'Name',
          flex: 1,
          options: {
            sort: true,
            customBodyRenderLite: (dataIndex: number) => {
              const row = props.reporting.bulk.paginatedBulkReportResults?.results[dataIndex];
              if (!row) return '';
              return (
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography>{row.name}</Typography>
                  {row.scheduleInstanceDetails && <ScheduleSend color="primary" />}
                </Box>
              );
            },
          },
        },
        {
          name: 'createdById',
          label: 'Created By',
          flex: 1,
          filterDataType: FilterDataType.numeric,
        },
        {
          name: 'reviewStatus',
          label: 'Scheduled Status',
          flex: 1,
          options: {
            filter: false,
            sort: false,
            customBodyRenderLite: (dataIndex: number) => {
              const scheduleInstanceDetails = props.reporting.bulk.paginatedBulkReportResults?.results[dataIndex]?.scheduleInstanceDetails;
              if (!scheduleInstanceDetails) {
                return <Chip label="Unavailable" color="error" variant="outlined" />;
              }
              const reviewStatus = SCHEDULED_INSTANCE_STATUS_NAMES[scheduleInstanceDetails?.reviewStatus ?? 'I'] ?? null;
              const color = SCHEDULED_INSTANCE_STATUS_COLORS[reviewStatus ?? 'In Review'];
              const failedEmails = scheduleInstanceDetails?.failedEmailSentRecords ?? 0;
              const failedDocuments = scheduleInstanceDetails?.failedDocumentUploadRecords ?? 0;
              return (
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip label={reviewStatus} color={color} />
                  {failedEmails > 0 && (
                    <Tooltip title={`${failedEmails} emails failed to send`}>
                      <Badge badgeContent={failedEmails} color="error" max={999}>
                        <EmailOutlined color="error" />
                      </Badge>
                    </Tooltip>
                  )}
                  {failedDocuments > 0 && (
                    <Tooltip title={`${failedDocuments} documents failed to upload`}>
                      <Badge badgeContent={failedDocuments} color="error" max={999}>
                        <CloudUploadOutlined color="error" />
                      </Badge>
                    </Tooltip>
                  )}
                </Box>
              );
            },
          },
        },
        {
          name: 'progress',
          label: 'Progress',
          flex: 1,
          options: {
            sort: false,
            filter: false,
            customBodyRenderLite: (dataIndex: number) => {
              const progress = props.reporting.bulk.paginatedBulkReportResults?.results[dataIndex]?.progress;
              if (!progress) return '';
              return `Completed ${progress.completedRecords} out of ${progress.totalRecords}`;
            },
          },
        },
        {
          name: 'updatedOn',
          label: 'Updated On',
          flex: 1,
          options: {
            filter: false,
            sort: false,
            customBodyRenderLite: (dataIndex: number) => {
              const row = props.reporting.bulk.paginatedBulkReportResults?.results[dataIndex];
              return row?.updatedOn ? getLocalDateTime(row.updatedOn.toString(), DateTimeFormat.DateTimeShort) : '';
            },
          },
        },
        {
          name: 'createdOn',
          label: 'Created On',
          flex: 1,
          options: {
            filter: false,
            sort: false,
            customBodyRenderLite: (dataIndex: number) => {
              const row = props.reporting.bulk.paginatedBulkReportResults?.results[dataIndex];
              return row?.createdOn ? getLocalDateTime(row.createdOn.toString(), DateTimeFormat.DateTimeShort) : '';
            },
          },
        },
        {
          name: '',
          label: '',
          options: {
            sort: false,
            filter: false,
            customBodyRenderLite: (dataIndex: number) => {
              const row = props.reporting.bulk.paginatedBulkReportResults?.results[dataIndex];
              if (!row) return null;

              return (
                <Box display="flex">
                  <Tooltip title="View">
                    <span>
                      <IconButton color="primary" onClick={() => handleView(row.id)}>
                        <Visibility />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        color="primary"
                        disabled={row.scheduleInstanceDetails?.reviewStatus === 'A' || row.scheduleInstanceDetails?.reviewStatus === 'R'}
                        onClick={() => handleDelete(row)}
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            },
          },
        },
      ],
      [props.reporting.bulk.paginatedBulkReportResults]
    );

    return columns;
  };

  return (
    <Paper elevation={4}>
      <ServerSideDataTable<BulkReportJob>
        pagedRequest={pagedRequest}
        handlePagedRequest={handlePagedRequest}
        pagedResult={props.reporting.bulk.paginatedBulkReportResults}
        loadingProgress={props.fetchBulkReportListProgress}
        columns={getReportingLibraryColumns(handleView, handleDelete)}
        options={tableOptions}
        csvDownload={() => {
          props.downloadBulkJobsCsv({ selectedRowIds: selectedRowIds, queryFields: pagedRequest.queryFields });
        }}
      />

      {deletingRow && (
        <DeleteBulkReportJobModal
          isOpen={deletingRow !== null}
          handleClose={() => setDeletingRow(null)}
          deleteBulkReportJob={deleteBulkReport}
          deleteBulkReportJobProgress={props.deleteBulkReportProgress}
          job={deletingRow}
        />
      )}
    </Paper>
  );
};
